import { useState } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, Plus, Truck, Phone, Mail, Info } from "lucide-react";
import { useCreateCarrier, type Carrier } from "@/api/carriers";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Form schema for adding a carrier
const formSchema = z.object({
  // Basic Information
  code: z
    .string()
    .min(1, "Code is required")
    .regex(
      /^[a-z0-9_]+$/,
      "Code must be lowercase alphanumeric with underscores"
    ),
  name: z.string().min(1, "Name is required"),

  // No `type` / `isLocalFleet` / `fleetOperator`: creating a carrier says WHO
  // it is, never HOW EZX buys from it — that is a SupplyChannel. The server's
  // create schema does not accept them, and zod silently strips what it does
  // not declare, so anything added back here is discarded without a word.
  maxDailyCapacity: z.number().min(0).optional(),
  vehicleCount: z.number().min(0).optional(),

  // No operatingHours: this form had no field for it and posted a hardcoded
  // 08:00-20:00 week in a shape nothing reads — long day keys with start/end,
  // where the only parser (WeeklyHoursSchema) is strict short keys with
  // open/close, and is applied to dropoff locations, never to a Carrier.

  // Contact Information
  contactEmail: z.string().email().optional().or(z.literal("")),
  contactPhone: z.string().optional(),
  dispatchEmail: z.string().email().optional().or(z.literal("")),

  // Branding
  logoUrl: z.string().url().optional().or(z.literal("")),

  // EZX's fee on this carrier. Required server-side (created atomically with the
  // carrier); admin sees the values here so the choice is intentional. Tune
  // later overrides can be set later from the pricing workspace.
  percent: z.number().min(0).default(0),
  feeFlatCents: z.number().min(0).default(0),

  // Status
  active: z.boolean().default(true),
});

type FormValues = z.infer<typeof formSchema>;

interface AddCarrierDialogProps {
  onSuccess?: () => void;
}

export function AddCarrierDialog({ onSuccess }: AddCarrierDialogProps) {
  const [open, setOpen] = useState(false);

  const createCarrierMutation = useCreateCarrier({
    onSuccess: (data) => {
      toast.success("Carrier Created", {
        description: `${data.name} has been added. Open its configuration to set up supply before creating dispatch centers.`,
      });
      form.reset();
      setOpen(false);
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(
        err.response?.data?.error ||
          "Failed to create carrier. Please try again."
      );
    },
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: "",
      name: "",
      maxDailyCapacity: 100,
      vehicleCount: 5,
      contactEmail: "",
      contactPhone: "",
      dispatchEmail: "",
      logoUrl: "",
      percent: 0,
      feeFlatCents: 0,
      active: true,
    },
  });

  const onSubmit = (values: FormValues) => {
    // Prepare the carrier data
    const carrierData: Partial<Carrier> & {
      code: string;
      name: string;
      dispatchEmail?: string;
      platformFee: {
        percent: number;
        flatCents: number;
      };
    } = {
      code: values.code,
      name: values.name,
      active: values.active,
      platformFee: {
        percent: values.percent,
        flatCents: values.feeFlatCents,
      },
    };

    // Add optional fields if provided
    if (values.maxDailyCapacity !== undefined)
      carrierData.maxDailyCapacity = values.maxDailyCapacity;
    if (values.vehicleCount !== undefined)
      carrierData.vehicleCount = values.vehicleCount;
    if (values.contactEmail) carrierData.contactEmail = values.contactEmail;
    if (values.contactPhone) carrierData.contactPhone = values.contactPhone;
    if (values.dispatchEmail) carrierData.dispatchEmail = values.dispatchEmail;
    if (values.logoUrl) carrierData.logoUrl = values.logoUrl;

    createCarrierMutation.mutate(carrierData);
  };

  // Preset configurations for quick setup
  const applyPreset = (preset: "local" | "ups" | "fedex") => {
    switch (preset) {
      case "local":
        form.setValue("code", "ez_fleet");
        form.setValue("name", "EZ Fleet");
        form.setValue("vehicleCount", 5);
        form.setValue("maxDailyCapacity", 100);
        break;
      case "ups":
        form.setValue("code", "ups");
        form.setValue("name", "UPS");
        break;
      case "fedex":
        form.setValue("code", "fedex");
        form.setValue("name", "FedEx");
        break;
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Carrier
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Carrier</DialogTitle>
          <DialogDescription>
            Who this carrier is. How EZX buys from it is a supply channel, set
            up separately — a new carrier has none and cannot be quoted yet.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 mt-4"
          >
            {/* Quick Presets */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="mt-2">
                <div className="space-y-2">
                  <p>Quick presets for common carriers:</p>
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => applyPreset("local")}
                    >
                      <Truck className="h-4 w-4 mr-1" />
                      Local Fleet
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => applyPreset("ups")}
                    >
                      UPS
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => applyPreset("fedex")}
                    >
                      FedEx
                    </Button>
                  </div>
                </div>
              </AlertDescription>
            </Alert>

            {/* Basic Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Configuration</CardTitle>
                <CardDescription>
                  Essential information about the carrier
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Carrier Code</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., ez_fleet, ups, fedex"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Unique identifier (lowercase, no spaces)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Carrier Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., Main Fleet, UPS, FedEx"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Capacity — accepted by the server for any carrier, so it is no
                longer hidden behind a carrier "type" that does not exist. */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  Capacity
                </CardTitle>
                <CardDescription>
                  Capacity this carrier can handle. Leave blank if not tracked.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="vehicleCount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Number of Vehicles</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value, 10))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="maxDailyCapacity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Daily Capacity</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="100"
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value, 10))
                            }
                          />
                        </FormControl>
                        <FormDescription>
                          Maximum orders per day
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="contactEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Contact Email
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="contact@carrier.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="contactPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          Contact Phone
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="+1-555-0100" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="dispatchEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Dispatch Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="dispatch@carrier.com"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Email for dispatch notifications
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="logoUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Logo URL</FormLabel>
                      <FormControl>
                        <Input
                          type="url"
                          placeholder="https://example.com/logo.png"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        URL to the carrier's logo image
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* No pickup/handling fee field: EZX's cut lives on PlatformFee
                (collected below), never on the carrier's identity. The server
                does not accept it. */}

            {/* EZX's fee -- required carrier-wide default. The server creates the
                row atomically with the carrier. Tune later from the pricing
                workspace; this dialog just sets the starting values. */}
            <Card>
              <CardHeader>
                <CardTitle>EZX fee</CardTitle>
                <CardDescription>
                  What EZX adds on top of this carrier&apos;s rates to reach the
                  customer price. Every carrier must have one. It defaults to
                  ZERO, which means EZX takes no commission until you change it.
                  Per-service overrides can be set later from the pricing
                  workspace.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="percent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Percentage of the carrier&apos;s rate (e.g. 10 = 10%)
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="0"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseFloat(e.target.value) || 0)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="feeFlatCents"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Processing Fee (cents)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="0"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseInt(e.target.value, 10) || 0)
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        Flat per-shipment fee, added on top of the percentage.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Status */}
            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Active</FormLabel>
                    <FormDescription>
                      Enable this carrier for shipments immediately
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={createCarrierMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createCarrierMutation.isPending}>
                {createCarrierMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Add Carrier
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
